import React, { useState, useEffect, useCallback } from 'react';
import { Navigate } from 'react-router-dom';
import AdminLayout from '../../../components/AdminLayout';
import ContentBox from '../../../components/ContentBox';
import {
    getPartners,
    createPartner,
    getPartnerIntegration,
    updatePartner,
    regeneratePartnerKey,
    createPartnerPreviewToken,
    deletePartner,
    getPartnerUploads,
} from '../../../services/api';
import { toast } from 'react-toastify';

const buildHandover = (i) => `FranklyCloud Image Upload: setup for ${i.partnerName}

Your visitors upload an image, it is checked automatically, and you get back a
URL on ${i.siteOrigin} that you can use anywhere on your site.

────────────────────────────────────────
1. YOUR KEY  (keep this on your server, never put it in a web page)
────────────────────────────────────────
${i.apiKey}

The part after the last underscore is your SECRET:
${i.secret}

────────────────────────────────────────
2. GENERATE A TOKEN  (on your server, once per upload)
────────────────────────────────────────
PHP:
  $keyId  = '${i.keyId}';
  $secret = '${i.secret}';
  $exp     = time() + ${i.tokenTtlSeconds};
  $tokenId = bin2hex(random_bytes(8));
  $sig     = hash_hmac('sha256', "$keyId.$exp.$tokenId", $secret);
  $token   = "$keyId.$exp.$tokenId.$sig";

Node.js:
  const crypto = require('crypto');
  const keyId = '${i.keyId}';
  const secret = '${i.secret}';
  const exp = Math.floor(Date.now() / 1000) + ${i.tokenTtlSeconds};
  const tokenId = crypto.randomBytes(8).toString('hex');
  const sig = crypto.createHmac('sha256', secret)
    .update(\`\${keyId}.\${exp}.\${tokenId}\`).digest('hex');
  const token = \`\${keyId}.\${exp}.\${tokenId}.\${sig}\`;

Python:
  import time, secrets, hmac, hashlib
  key_id = '${i.keyId}'
  secret = '${i.secret}'
  exp = int(time.time()) + ${i.tokenTtlSeconds}
  token_id = secrets.token_hex(8)
  sig = hmac.new(secret.encode(), f"{key_id}.{exp}.{token_id}".encode(),
                 hashlib.sha256).hexdigest()
  token = f"{key_id}.{exp}.{token_id}.{sig}"

────────────────────────────────────────
3. PUT THE UPLOADER ON YOUR PAGE
────────────────────────────────────────
<iframe src="${i.widgetUrl}?token=YOUR_TOKEN_HERE"
        style="width:100%;height:290px;border:0"></iframe>

<script>
window.addEventListener('message', function (e) {
  if (e.origin !== '${i.siteOrigin}') return;
  if (!e.data || e.data.source !== 'franklycloud') return;

  if (e.data.type === 'uploaded') {
    console.log('Image URL:', e.data.url);
  }
  if (e.data.type === 'error') {
    console.log('Rejected:', e.data.message);
  }
});
</script>

────────────────────────────────────────
4. THINGS TO KNOW
────────────────────────────────────────
- One token = one upload. Generate a fresh token each time.
- Tokens expire after ${Math.round(i.tokenTtlSeconds / 60)} minutes.
- Only these addresses can show the uploader:
${i.allowedOrigins.map(o => `    ${o}`).join('\n')}
- Daily limit: ${i.dailyQuota} uploads.
- Accepted: JPEG, PNG, GIF, WebP, up to 15MB. Images are stored at their
  original resolution and converted to WebP.
- Every image is automatically checked before it is accepted. If it is
  rejected your visitor is told, and nothing is stored.

Prefer to upload from your own server instead of the widget? POST
multipart/form-data with an "image" field to:
  ${i.uploadUrl}
with the header:  Authorization: Bearer ${i.apiKey}
`;

const STATUS_STYLES = {
    approved: 'bg-green-100 text-green-800',
    rejected: 'bg-red-100 text-red-800',
    pending: 'bg-gray-100 text-gray-600',
};

function FranklyCloudPartners({ userData }) {
    const [partners, setPartners] = useState([]);
    const [loading, setLoading] = useState(true);
    const [configured, setConfigured] = useState(true);
    const [imageProcessing, setImageProcessing] = useState(null);

    const [name, setName] = useState('');
    const [origins, setOrigins] = useState('');
    const [quota, setQuota] = useState(500);
    const [creating, setCreating] = useState(false);

    const [integration, setIntegration] = useState(null);
    const [pendingDelete, setPendingDelete] = useState(null);
    const [busyId, setBusyId] = useState(null);

    const [previewUrl, setPreviewUrl] = useState(null);
    const [previewName, setPreviewName] = useState('');

    const [uploads, setUploads] = useState([]);
    const [uploadsLoading, setUploadsLoading] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await getPartners();
            if (res?.success) {
                setPartners(res.partners || []);
                setConfigured(res.configured !== false);
                setImageProcessing(res.imageProcessing || null);
            }
        } catch {
            toast.error('Failed to load partners');
        } finally {
            setLoading(false);
        }
    }, []);

    const loadUploads = useCallback(async () => {
        setUploadsLoading(true);
        try {
            const res = await getPartnerUploads(1);
            if (res?.success) setUploads(res.uploads || []);
        } catch {
            toast.error('Failed to load upload log');
        } finally {
            setUploadsLoading(false);
        }
    }, []);

    useEffect(() => { load(); loadUploads(); }, [load, loadUploads]);

    const handleCreate = async (e) => {
        e.preventDefault();
        if (!name.trim() || !origins.trim() || creating) return;
        setCreating(true);
        try {
            const res = await createPartner({ name: name.trim(), origins: origins.trim(), dailyQuota: quota });
            if (res?.success) {
                setPartners(prev => [res.partner, ...prev]);
                setIntegration(res.integration);
                setName('');
                setOrigins('');
                setQuota(500);
                toast.success(`${res.partner.name} added`);
            } else {
                toast.error(res?.message || 'Failed to add partner');
            }
        } catch (err) {
            toast.error(err?.status === 500 ? 'API is missing PARTNER_SECRET_KEY' : 'Failed to add partner');
        } finally {
            setCreating(false);
        }
    };

    const handleToggle = async (partner) => {
        setBusyId(partner.id);
        try {
            await updatePartner(partner.id, { enabled: !partner.enabled });
            setPartners(prev => prev.map(p => p.id === partner.id ? { ...p, enabled: !p.enabled } : p));
            toast.success(`${partner.name} ${partner.enabled ? 'disabled' : 'enabled'}`);
        } catch {
            toast.error('Failed to update partner');
        } finally {
            setBusyId(null);
        }
    };

    const handleShowDetails = async (partner) => {
        setBusyId(partner.id);
        try {
            const res = await getPartnerIntegration(partner.id);
            if (res?.success) setIntegration(res.integration);
            else toast.error(res?.message || 'Could not load details');
        } catch {
            toast.error('Could not load details');
        } finally {
            setBusyId(null);
        }
    };

    const handlePreview = async (partner) => {
        setBusyId(partner.id);
        try {
            const res = await createPartnerPreviewToken(partner.id);
            if (res?.success) {
                setPreviewName(partner.name);
                setPreviewUrl(`/franklycloud/widget?token=${encodeURIComponent(res.token)}`);
            } else {
                toast.error(res?.message || 'Could not start preview');
            }
        } catch {
            toast.error('Could not start preview');
        } finally {
            setBusyId(null);
        }
    };

    const handleRegenerate = async (partner) => {
        if (!window.confirm(`Generate a new key for ${partner.name}? Their current key stops working immediately and you must send them the new details.`)) return;
        setBusyId(partner.id);
        try {
            const res = await regeneratePartnerKey(partner.id);
            if (res?.success) {
                setIntegration(res.integration);
                setPartners(prev => prev.map(p => p.id === partner.id ? { ...p, keyId: res.integration.keyId } : p));
                toast.success('New key generated');
            }
        } catch {
            toast.error('Failed to regenerate key');
        } finally {
            setBusyId(null);
        }
    };

    const handleDelete = async (partner) => {
        setBusyId(partner.id);
        try {
            const res = await deletePartner(partner.id);
            setPartners(prev => prev.filter(p => p.id !== partner.id));
            setPendingDelete(null);
            if (integration?.keyId === partner.keyId) setIntegration(null);
            toast.success(`${partner.name} removed (${res?.removedImages || 0} images deleted)`);
        } catch {
            toast.error('Failed to remove partner');
        } finally {
            setBusyId(null);
        }
    };

    const copyHandover = () => {
        const text = buildHandover(integration);
        navigator.clipboard.writeText(text)
            .then(() => toast.success('Setup details copied, paste this to your partner'))
            .catch(() => toast.error('Could not copy to clipboard'));
    };

    if (!userData || !userData.hasAdminRole) {
        return <Navigate to="/" replace />;
    }

    return (
        <AdminLayout>
            <div className="flex flex-col gap-4">

                {imageProcessing && imageProcessing.available === false && (
                    <ContentBox theme="default" tint="red" header="Image Processing Unavailable">
                        <p className="text-sm mb-2">
                            The API cannot load <strong>sharp</strong>, so no image uploads will work anywhere on
                            the site until this is resolved. Redeploy the API so it reinstalls dependencies.
                        </p>
                        <pre className="text-[11px] bg-white border border-black/20 p-2 overflow-x-auto whitespace-pre-wrap">
{`platform: ${imageProcessing.platform}
code:     ${imageProcessing.code || '(none)'}
error:    ${imageProcessing.error}`}
                        </pre>
                    </ContentBox>
                )}

                {!configured && (
                    <ContentBox theme="default" tint="red" header="Setup Required">
                        <p className="text-sm">
                            The API is missing <strong>PARTNER_SECRET_KEY</strong> and/or <strong>PUBLIC_SITE_URL</strong>.
                            Partner keys cannot be generated until both are set in the API environment.
                        </p>
                    </ContentBox>
                )}

                <ContentBox theme="default" tint="blue" header="Add a Partner Website">
                    <form onSubmit={handleCreate} className="flex flex-col gap-3">
                        <div className="flex flex-col gap-1">
                            <label className="text-xs font-bold uppercase tracking-wide text-gray-600">Partner name</label>
                            <input
                                type="text"
                                value={name}
                                onChange={e => setName(e.target.value)}
                                placeholder="Habbo Fansite"
                                maxLength={160}
                                className="border border-black/20 px-3 py-2 text-sm"
                            />
                        </div>

                        <div className="flex flex-col gap-1">
                            <label className="text-xs font-bold uppercase tracking-wide text-gray-600">
                                Website address we accept from
                            </label>
                            <input
                                type="text"
                                value={origins}
                                onChange={e => setOrigins(e.target.value)}
                                placeholder="https://partnersite.com"
                                className="border border-black/20 px-3 py-2 text-sm"
                            />
                            <p className="text-xs text-gray-500">
                                Separate multiple addresses with commas. Include https://. Add the www version too if they use it.
                            </p>
                        </div>

                        <div className="flex flex-col gap-1 max-w-[200px]">
                            <label className="text-xs font-bold uppercase tracking-wide text-gray-600">Daily upload limit</label>
                            <input
                                type="number"
                                value={quota}
                                min={1}
                                max={100000}
                                onChange={e => setQuota(e.target.value)}
                                className="border border-black/20 px-3 py-2 text-sm"
                            />
                        </div>

                        <button
                            type="submit"
                            disabled={creating || !name.trim() || !origins.trim()}
                            className="self-start px-4 py-2 bg-coal text-offwhite font-['Jersey_10'] uppercase text-[16px] tracking-[1px] disabled:opacity-40"
                            style={{ boxShadow: '0 2px 0 0 rgba(0,0,0,0.2)' }}
                        >
                            {creating ? 'Adding…' : 'Add Partner'}
                        </button>
                    </form>
                </ContentBox>

                {integration && (
                    <ContentBox theme="default" tint="green" header={`Send These Details to ${integration.partnerName}`}>
                        <div className="flex flex-col gap-3">
                            <div className="flex items-center gap-2 flex-wrap">
                                <button
                                    onClick={copyHandover}
                                    className="px-4 py-2 bg-green-600 text-white font-['Jersey_10'] uppercase text-[16px] tracking-[1px]"
                                    style={{ boxShadow: '0 2px 0 0 rgba(0,0,0,0.2)' }}
                                >
                                    Copy Everything
                                </button>
                                <button
                                    onClick={() => setIntegration(null)}
                                    className="px-3 py-2 border border-black/20 text-sm font-bold text-gray-600 hover:bg-black/5"
                                >
                                    Close
                                </button>
                                <span className="text-xs text-gray-500">
                                    Send this to the partner. It contains their key, so share it privately.
                                </span>
                            </div>

                            <textarea
                                readOnly
                                value={buildHandover(integration)}
                                onFocus={e => e.target.select()}
                                className="w-full h-[420px] border border-black/20 p-3 font-mono text-[11px] leading-snug bg-white"
                            />
                        </div>
                    </ContentBox>
                )}

                {previewUrl && (
                    <ContentBox theme="default" tint="gold" header={`Preview: ${previewName}`}>
                        <div className="flex flex-col gap-2">
                            <div className="flex items-center gap-2 flex-wrap">
                                <button
                                    onClick={() => setPreviewUrl(null)}
                                    className="px-3 py-2 border border-black/20 text-sm font-bold text-gray-600 hover:bg-black/5"
                                >
                                    Close Preview
                                </button>
                                <span className="text-xs text-gray-500">
                                    This is exactly what the partner's visitors see. Uploading here is real,
                                    it counts against their quota and appears in the log below.
                                </span>
                            </div>
                            <iframe
                                title="FranklyCloud upload preview"
                                src={previewUrl}
                                className="w-full h-[290px] border border-black/20 bg-offwhite"
                            />
                        </div>
                    </ContentBox>
                )}

                <ContentBox theme="default" tint="red" header="Partner Websites">
                    {loading ? (
                        <p className="text-sm text-gray-500">Loading…</p>
                    ) : partners.length === 0 ? (
                        <p className="text-sm text-gray-500">No partner websites yet. Add one above.</p>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm border-collapse">
                                <thead>
                                    <tr className="border-b-2 border-black/20 text-left">
                                        <th className="py-2 pr-3">Partner</th>
                                        <th className="py-2 pr-3">Accepted From</th>
                                        <th className="py-2 pr-3">Uploads</th>
                                        <th className="py-2 pr-3">Status</th>
                                        <th className="py-2">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {partners.map(p => (
                                        <tr key={p.id} className="border-b border-black/10 align-top">
                                            <td className="py-2 pr-3">
                                                <div className="font-bold">{p.name}</div>
                                                <div className="text-xs text-gray-500">
                                                    {p.lastUsedAt ? `Last used ${new Date(p.lastUsedAt).toLocaleDateString()}` : 'Never used'}
                                                </div>
                                            </td>
                                            <td className="py-2 pr-3 text-xs">
                                                {p.allowedOrigins.map(o => <div key={o}>{o}</div>)}
                                            </td>
                                            <td className="py-2 pr-3 text-xs">
                                                <div>{p.uploadsToday} today / {p.dailyQuota}</div>
                                                <div className="text-gray-500">{p.totalUploads} total</div>
                                                {p.rejectedUploads > 0 && (
                                                    <div className="text-red-600">{p.rejectedUploads} rejected</div>
                                                )}
                                            </td>
                                            <td className="py-2 pr-3">
                                                <span className={`px-2 py-0.5 text-xs font-bold ${p.enabled ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                                                    {p.enabled ? 'Enabled' : 'Disabled'}
                                                </span>
                                            </td>
                                            <td className="py-2">
                                                {pendingDelete === p.id ? (
                                                    <div className="flex flex-col gap-1">
                                                        <span className="text-xs text-red-600 font-bold">
                                                            Remove and delete their images?
                                                        </span>
                                                        <div className="flex gap-1">
                                                            <button
                                                                onClick={() => handleDelete(p)}
                                                                disabled={busyId === p.id}
                                                                className="px-2 py-1 bg-red-600 text-white text-xs font-bold uppercase"
                                                            >
                                                                Remove
                                                            </button>
                                                            <button
                                                                onClick={() => setPendingDelete(null)}
                                                                className="px-2 py-1 border border-black/20 text-xs font-bold"
                                                            >
                                                                Cancel
                                                            </button>
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <div className="flex gap-1 flex-wrap">
                                                        <button
                                                            onClick={() => handleShowDetails(p)}
                                                            disabled={busyId === p.id}
                                                            className="px-2 py-1 bg-coal text-offwhite text-xs font-bold uppercase disabled:opacity-40"
                                                        >
                                                            Details
                                                        </button>
                                                        <button
                                                            onClick={() => handlePreview(p)}
                                                            disabled={busyId === p.id || !p.enabled}
                                                            className="px-2 py-1 bg-gold text-coal text-xs font-bold uppercase disabled:opacity-40"
                                                        >
                                                            Preview
                                                        </button>
                                                        <button
                                                            onClick={() => handleToggle(p)}
                                                            disabled={busyId === p.id}
                                                            className="px-2 py-1 border border-black/20 text-xs font-bold uppercase disabled:opacity-40"
                                                        >
                                                            {p.enabled ? 'Disable' : 'Enable'}
                                                        </button>
                                                        <button
                                                            onClick={() => handleRegenerate(p)}
                                                            disabled={busyId === p.id}
                                                            className="px-2 py-1 border border-black/20 text-xs font-bold uppercase disabled:opacity-40"
                                                        >
                                                            New Key
                                                        </button>
                                                        <button
                                                            onClick={() => setPendingDelete(p.id)}
                                                            disabled={busyId === p.id}
                                                            className="px-2 py-1 bg-red-600 text-white text-xs font-bold uppercase disabled:opacity-40"
                                                        >
                                                            Remove
                                                        </button>
                                                    </div>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </ContentBox>

                <ContentBox theme="default" tint="blue" header="Recent Partner Uploads">
                    {uploadsLoading ? (
                        <p className="text-sm text-gray-500">Loading…</p>
                    ) : uploads.length === 0 ? (
                        <p className="text-sm text-gray-500">Nothing uploaded by partners yet.</p>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm border-collapse">
                                <thead>
                                    <tr className="border-b-2 border-black/20 text-left">
                                        <th className="py-2 pr-3">When</th>
                                        <th className="py-2 pr-3">Partner</th>
                                        <th className="py-2 pr-3">Result</th>
                                        <th className="py-2 pr-3">Image</th>
                                        <th className="py-2">Their User</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {uploads.map(u => (
                                        <tr key={u.id} className="border-b border-black/10">
                                            <td className="py-2 pr-3 text-xs whitespace-nowrap">
                                                {new Date(u.createdAt).toLocaleString()}
                                            </td>
                                            <td className="py-2 pr-3">{u.partnerName}</td>
                                            <td className="py-2 pr-3">
                                                <span className={`px-2 py-0.5 text-xs font-bold ${STATUS_STYLES[u.status] || 'bg-yellow-100 text-yellow-800'}`}>
                                                    {u.status}
                                                </span>
                                            </td>
                                            <td className="py-2 pr-3">
                                                {u.url ? (
                                                    <a href={u.url} target="_blank" rel="noreferrer">
                                                        <img src={u.url} alt="" className="h-10 w-10 object-cover border border-black/20" />
                                                    </a>
                                                ) : (
                                                    <span className="text-xs text-gray-400">-</span>
                                                )}
                                            </td>
                                            <td className="py-2 text-xs text-gray-600">{u.endUserRef || '-'}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </ContentBox>

            </div>
        </AdminLayout>
    );
}

export default FranklyCloudPartners;
